import React, { useMemo } from 'react';
import useSmartScroll from '../hooks/useSmartScroll';
import '../styles/components/pageTemplate.css';

interface PageTemplateProps {
  // Section 1: App Title (always shown from header)
  
  // Section 2: Page Title
  pageTitle?: string;
  
  // Section 3: Page Summary
  pageSummary?: string;

  // Optional Section: Icon area shown just below the title/summary
  iconArea?: React.ReactNode;
  
  // Section 4: Input Area
  inputArea?: React.ReactNode;
  
  // Section 5: Body
  children: React.ReactNode;
  
  // Section 6: Bottom Nav (always shown from main layout)
  
  // Configuration options
  showPageHeader?: boolean;
  showIconArea?: boolean;
  showInputArea?: boolean;
  hasGrayBackground?: boolean;
  className?: string;
  // Progressive enhancement: optional smart scroll
  enableSmartScroll?: boolean;
  // Control stickiness (defaults keep current behavior)
  stickyHeader?: boolean;
  stickyInput?: boolean;
}

const PageTemplate: React.FC<PageTemplateProps> = ({
  pageTitle,
  pageSummary,
  iconArea,
  inputArea,
  children,
  showPageHeader = true,
  showIconArea = false,
  showInputArea = false,
  hasGrayBackground = false,
  className = '',
  enableSmartScroll = false,
  stickyHeader = true,
  stickyInput = true,
}) => {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  
  // Progressive enhancement: only runs if enabled for this page
  useSmartScroll(enableSmartScroll);
  
  // Header is always fixed; do not toggle non-sticky header state anymore

  const containerClass = useMemo(() => {
    return `page-template ${className} ${!stickyInput ? 'non-sticky-input' : ''}`.trim();
  }, [className, stickyInput]);

  return (
    <div className={containerClass}>
      {/* Section 2 & 3: Page Header (Title + Summary) */}
      {showPageHeader && (pageTitle || pageSummary) && (
        <div className={`page-template-header ${hasGrayBackground ? 'with-background' : ''}`}>
          {pageTitle && (
            <h1 className="page-template-title">{pageTitle}</h1>
          )}
          {pageSummary && (
            <p className="page-template-summary">{pageSummary}</p>
          )}
        </div>
      )}

      {/* Optional: Icon area directly under the title/summary */}
      {showIconArea && iconArea && (
        <div className="page-template-icon" aria-hidden="true">
          {iconArea}
        </div>
      )}
      
      {/* Section 4: Input Area */}
      {showInputArea && inputArea && (
        <div className="page-template-input">
          {inputArea}
        </div>
      )}
      
      {/* Section 5: Body Content */}
      <div className="page-template-body">
        {children}
      </div>
    </div>
  );
};

export default PageTemplate;
